import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { OpenFundRequestBasicDetailsDtoIn } from '@/models/in/OpenFundRequestBasicDetailsDtoIn';
import { NewCoordinatorDtoIn } from '@/models/in/NewCoordinatorDtoIn';
import { OpenFundRequestStatus } from '@/models/common/OpenFundRequestStatus';
import { SyncStatus } from '@/models/common/SyncStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';
import CoordinatorsDialog from './CoordinatorsDialog';

const LOCAL_STORAGE_KEY = 'openFundRequestsTableContext';
const DENSITY_STORAGE_KEY = 'table.density.openFundRequests';

const STATUS_TABS: OpenFundRequestStatus[] = [
  OpenFundRequestStatus.PendingApproval,
  OpenFundRequestStatus.Incomplete,
  OpenFundRequestStatus.ReturnedFromIncomplete,
  OpenFundRequestStatus.Approved,
  OpenFundRequestStatus.CancelledByUser,
  OpenFundRequestStatus.Rejected,
  OpenFundRequestStatus.Completed,
];

function getInitialStatus(): OpenFundRequestStatus {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.status && Object.values(OpenFundRequestStatus).includes(parsed.status)) {
        return parsed.status;
      }
    }
  } catch {
    // ignore parse errors
  }
  return OpenFundRequestStatus.PendingApproval;
}

function saveStatus(status: OpenFundRequestStatus) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ status }));
}

const openFundRequestSearch: FilterFn<OpenFundRequestBasicDetailsDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const item = row.original;

  const coordinatorNames = item.coordinators
    ?.map((c) => `${c.firstName} ${c.lastName}`)
    .join(' ') ?? '';

  const searchableFields = [
    item.requestId?.toString(),
    item.fundId?.toString(),
    item.fundName,
    coordinatorNames,
  ];

  return searchableFields.some((field) => field?.toLowerCase().includes(searchValue));
};

const OpenFundRequestsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedStatus, setSelectedStatus] = useState<OpenFundRequestStatus>(getInitialStatus);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  const [coordinatorsDialogOpen, setCoordinatorsDialogOpen] = useState(false);
  const [selectedCoordinators, setSelectedCoordinators] = useState<NewCoordinatorDtoIn[]>([]);

  const { data, isLoading } = useGetAuthApi<OpenFundRequestBasicDetailsDtoIn[]>(
    `funds/creation/open-fund-requests-list?status=${selectedStatus}`
  );

  const handleStatusChange = useCallback((value: string) => {
    const newStatus = value as OpenFundRequestStatus;
    setSelectedStatus(newStatus);
    saveStatus(newStatus);
  }, []);

  const handleShowCoordinators = useCallback((coordinators: NewCoordinatorDtoIn[]) => {
    setSelectedCoordinators(coordinators);
    setCoordinatorsDialogOpen(true);
  }, []);

  const handleViewRequest = useCallback((requestId: number) => {
    navigate('/management/open-fund-request-view', {
      state: { requestId },
    });
  }, [navigate]);

  const showFundIdColumn = selectedStatus === OpenFundRequestStatus.Approved || selectedStatus === OpenFundRequestStatus.Completed;
  const showSyncStatusColumn = selectedStatus === OpenFundRequestStatus.Approved;

  // All columns are always defined (hooks always called in same order),
  // then filtered by visibility to avoid React hooks order violations.
  const allColumns: (ColumnDef<OpenFundRequestBasicDetailsDtoIn> & { _visible?: boolean })[] = [
    {
      id: 'requestId',
      header: Col(t('openFundRequests.requestNumber')),
      cell: Cell(cellType.Number),
      accessorKey: 'requestId',
      meta: { className: 'max-w-[100px] min-w-[100px] w-[100px]' },
    },
    {
      id: 'creationTime',
      header: Col(t('openFundRequests.submissionDate')),
      cell: Cell(cellType.Date),
      accessorKey: 'creationTime',
      meta: { className: 'max-w-[140px] min-w-[140px] w-[140px]' },
    },
    {
      id: 'fundId',
      header: Col(selectedStatus === OpenFundRequestStatus.Approved
        ? t('openFundRequests.desiredFundNumber')
        : t('openFundRequests.fundNumber')),
      cell: Cell(cellType.Number),
      accessorKey: 'fundId',
      meta: { className: 'max-w-[100px] min-w-[100px] w-[100px]' },
      _visible: showFundIdColumn,
    },
    {
      id: 'fundName',
      header: Col(t('openFundRequests.fundName')),
      cell: Cell(cellType.String),
      accessorKey: 'fundName',
      meta: { className: 'max-w-[200px] min-w-[200px] w-[200px]' },
    },
    {
      id: 'fundClassification',
      header: Col(t('openFundRequests.fundClassification')),
      cell: ({ getValue }) => <div>{t(`openFundRequests.fundClassificationNames.${getValue()}`)}</div>,
      accessorKey: 'fundClassification',
      meta: { className: 'max-w-[120px] min-w-[120px] w-[120px]' },
    },
    {
      id: 'coordinators',
      header: Col(t('openFundRequests.coordinators')),
      cell: ({ row }) => {
        const coordinators = row.original.coordinators;
        if (!coordinators || coordinators.length === 0) return null;
        const firstCoord = `${coordinators[0].firstName} ${coordinators[0].lastName}`;
        const label = coordinators.length > 1
          ? `${firstCoord} (${t('openFundRequests.andMore', { count: coordinators.length - 1 })})`
          : firstCoord;

        return (
          <button
            className="body-md-link text-(--font-link) cursor-pointer p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleShowCoordinators(coordinators);
            }}
          >
            {label}
          </button>
        );
      },
      enableSorting: false,
      meta: { className: 'max-w-[180px] min-w-[180px] w-[180px]' },
    },
    {
      id: 'syncStatus',
      header: Col(t('openFundRequests.syncStatus')),
      cell: ({ getValue }) => {
        const value = getValue() as SyncStatus | undefined;
        if (!value) return null;
        return <div>{t(`openFundRequests.syncStatusValues.${value}`)}</div>;
      },
      accessorKey: 'syncStatus',
      meta: { className: 'max-w-[130px] min-w-[130px] w-[130px]' },
      _visible: showSyncStatusColumn,
    },
    {
      id: 'view',
      header: () => <span>{t('openFundRequests.view')}</span>,
      cell: ({ row }) => (
        <button
          className="body-md-link text-(--font-link) cursor-pointer p-0"
          onClick={(e) => {
            e.stopPropagation();
            handleViewRequest(row.original.requestId);
          }}
        >
          {t('openFundRequests.view')}
        </button>
      ),
      enableSorting: false,
      meta: { className: 'max-w-[80px] min-w-[80px] w-[80px]', lockLayout: true },
    },
  ];

  const columns = allColumns.filter((col) => col._visible !== false);

  return (
    <div>
      <CoordinatorsDialog
        open={coordinatorsDialogOpen}
        setOpen={setCoordinatorsDialogOpen}
        coordinators={selectedCoordinators}
      />
      <Tabs
        value={selectedStatus}
        onValueChange={handleStatusChange}
        style={{ direction: 'rtl' }}
        className="mb-6"
      >
        <TabsList>
          {STATUS_TABS.map((status) => (
            <TabsTrigger key={status} value={status}>
              {t(`openFundRequests.status.${status}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <DataTable
        title={t('openFundRequests.title')}
        data={data ?? []}
        isLoading={isLoading}
        columns={columns}
        sortBy={{ id: 'requestId', desc: true }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="openFundRequests"
        searchPlaceholder="openFundRequests.searchShort"
        searchTooltip="openFundRequests.searchPlaceholder"
        tableHeight="calc(100vh - 380px)"
        customGlobalSearch={openFundRequestSearch}
      />
    </div>
  );
};

export default OpenFundRequestsListPage;
